import {
  customElement,
  type TemplateResult,
  state,
  html,
  property,
  css,
  cssManager,
} from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';

import { demoFunc }  from './dees-input-typelist.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

@customElement('dees-input-typelist')
export class DeesInputTypelist extends DeesInputBase<DeesInputTypelist> {
  public static demo = demoFunc;
  public static demoGroups = ['Input'];


  // INSTANCE

  @property({ type: Array })
  accessor value: string[] = [];

  @state()
  accessor inputValue: string = '';


  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      :host {
        color: var(--dees-color-text-primary);
        --typelist-hover-filter: brightness(1.05);
      }
      :host([gobright]) {
        --typelist-hover-filter: brightness(0.98);
      }
      .mainbox {
        border-radius: 3px;
        background: var(--dees-color-bg-secondary);
        overflow: hidden;
        border-top: 1px solid var(--dees-color-border-default);
        border-bottom: 1px solid var(--dees-color-border-default);
        border-right: 1px solid var(--dees-color-border-default);
        border-left: 1px solid var(--dees-color-border-default);
        box-shadow: var(--dees-shadow-xs);
        transition: all 0.2s;
        position: relative;
      }
      
      .mainbox:hover {
        filter: var(--typelist-hover-filter);
      }
      
      .mainbox:focus-within {
        outline: 2px solid var(--dees-color-accent-primary);
        outline-offset: -2px;
      }

      .tags {
        padding: 16px;
        cursor: default;
      }

      .notags {
        text-align: center;
        color: var(--dees-color-text-muted);
        font-size: 13px;
        font-style: italic;
      }

      input {
        display: block;
        box-sizing: border-box;
        background: var(--dees-color-code-block-bg);
        width: 100%;
        outline: none;
        border: none;
        color: inherit;
        padding: 0px 16px;
        overflow: hidden;
        line-height: 32px;
        height: 0px;
        transition: height 0.2s;
        border-top: 1px solid var(--dees-color-border-default);
      }

      input:focus {
        height: 32px;
        background: var(--dees-color-code-block-bg);
      }
      
      input::placeholder {
        color: var(--dees-color-text-muted);
      }

      .tag {
        display: inline-block;
        background: var(--dees-color-badge-success-bg);
        color: var(--dees-color-badge-success-fg);
        padding: 4px 10px;
        border-radius: 4px;
        margin: 3px;
        font-size: 13px;
        font-weight: 500;
        transition: all 0.2s;
        border: 1px solid color-mix(in srgb, var(--dees-color-badge-success-fg) 30%, transparent);
      }
      
      .tag:hover {
        background: color-mix(in srgb, var(--dees-color-badge-success-fg) 15%, var(--dees-color-badge-success-bg));
        transform: translateY(-1px);
      }

      .tag .remove {
        margin-left: 8px;
        cursor: pointer;
        opacity: 0.7;
        font-weight: 700;
        font-size: 16px;
        line-height: 1;
        transition: opacity 0.2s;
      }

      .tag .remove:hover {
        opacity: 1;
        color: var(--dees-color-text-error);
      }
      
      /* Disabled state */
      :host([disabled]) .mainbox {
        opacity: 0.6;
        cursor: not-allowed;
      }
      
      :host([disabled]) .tags {
        cursor: not-allowed;
      }
      
      :host([disabled]) .tag {
        pointer-events: none;
      }
      
      :host([disabled]) input {
        cursor: not-allowed;
        background: var(--dees-color-code-block-bg);
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="input-wrapper">
        <dees-label .label=${this.label} .infoText=${this.infoText}></dees-label>
        <div class="mainbox">
          <div class="tags" @click=${() => {
            this.shadowRoot!.querySelector('input')!.focus();
          }}>
            ${this.value.length === 0
              ? html`<div class="notags">No tags yet</div>`
              : this.value.map(
                  (tag) => html`
                    <span class="tag">
                      ${tag}
                      <span class="remove" @click=${(e: Event) => {
                        e.stopPropagation();
                        this.removeTag(tag);
                      }}>×</span>
                    </span>
                  `
                )}
          </div>
          <input
            type="text"
            placeholder="Type, press Enter to add it..."
            .value=${this.inputValue}
            @input=${(e: InputEvent) => {
              this.inputValue = (e.target as HTMLInputElement).value;
            }}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === 'Enter' && this.inputValue.trim()) {
                e.preventDefault();
                this.addTag(this.inputValue.trim());
              }
            }}
            .disabled=${this.disabled}
          />
        </div>
        ${this.renderDescription()}
      </div>
    `;
  }

  private addTag(tag: string) {
    if (!this.value.includes(tag)) {
      this.value = [...this.value, tag];
      this.inputValue = '';
      this.changeSubject.next(this);
    }
  }

  private removeTag(tag: string) {
    this.value = this.value.filter((t) => t !== tag);
    this.changeSubject.next(this);
  }

  public getValue(): string[] {
    return this.value;
  }

  public setValue(value: string[]): void {
    this.value = value;
  }
}
